/** What a decision moves, as a small table under its control: Measure / Change / Year-end (the memo's own figures, `effectTableOf`). */
import { EFFECT_BASIS, SCORE_ALONE, SCORE_IN_PLAN, measureKpiOf, type EffectTable } from "./effectTable";
import { GxKpiLabel } from "../theme/GxTerm";
import { labelOf } from "../format/labels";
import { GxTable, type GxColumn } from "../theme/GxTable";
import { formatUsdPerShare } from "../format";
import "./effect.css";

// The cells are the memo's own printed readings (a percentage-point move, a level), each row in its own unit, so the figures stay as printed and the row says its unit.
const COLUMNS: readonly GxColumn[] = [{ key: "measure", label: "Measure" }, { key: "change", label: "Change", kind: "number", phone: "line" }, { key: "yearEnd", label: "Year-end", kind: "number", phone: "line" }];

/** `scoreUsd`: the decision's own three-year value in dollars a share (the panel's unit), when the preview has it: the score row then reads "$ a share" first, the points muted after; the note's own dollars (`usd`) when the preview has none. */
/** `reconcile`: one line (`valueBasisLine`) saying how this decision\u2019s other readings on the page differ (by basis only), under the basis caption. */
export function EffectTableView({ table, testId, scoreUsd = null, reconcile = null }: { readonly table: EffectTable; readonly testId: string; readonly scoreUsd?: number | null; readonly reconcile?: string | null }) {
  return (
    <div className="gx-effect" data-testid={testId}>
      <p className="gx-effect-cap" data-testid={`${testId}.basisnote`}>{EFFECT_BASIS}</p>
      {reconcile === null ? null : <p className="gx-effect-cap" data-testid={`${testId}.reconcile`}>{reconcile}</p>}
      <GxTable
        caption="This year: what this decision moves" columns={COLUMNS} dense phone="stack"
        rows={table.rows.map((r) => ({
          key: r.measure,
          cells: {
            measure: (() => { const kpi = measureKpiOf(r.measure); return kpi === null ? r.measure : <GxKpiLabel kpi={kpi}>{labelOf(kpi)}</GxKpiLabel>; })(),
            change: r.measure === "Projected score" && (scoreUsd !== null || r.usd !== undefined)
              ? <span className="gx-effect-cell"><span data-testid={`${testId}.usd`}>{scoreUsd !== null ? formatUsdPerShare(scoreUsd, { signed: true }) : r.usd} a share</span> <span className="gx-faint gx-effect-pts">({r.change})</span> <span className="gx-faint gx-effect-basis" data-testid={`${testId}.basis`}>{scoreUsd !== null ? SCORE_ALONE : SCORE_IN_PLAN}</span></span>
              : r.change,
            yearEnd: r.yearEnd,
          },
        }))}
      />
    </div>
  );
}
